import {
  useMemo, useRef
} from 'react';
import { ApolloError } from '@apollo/client';
import { Categories } from '@pizza-hut-us-development/client-core';
import { MenuRecipe } from '@/menu/pizza/pizzaMenuTypes';
import useCCGetDealsMenu from '@/clientCore/temporaryTransformationalHooks/useCCGetDealsMenu';
import { UPDATED_LINEUP_TILE } from '@/deals/constants';
import { isLocalizedMenuPageSection, useCCGetProducts } from '@/clientCore/temporaryTransformationalHooks/useCCGetProducts';
import useDealTilePositioning from './hooks/useDealTilePositioning';
import { useGetProductsToHide } from '@/clientCore/menu/hooks/useGetProductsToHide';

interface DealMenuData {
  deals: MenuRecipe[];
  loading: boolean;
  refetch?: () => void;
}

const useDealMenu = (): DealMenuData => {
  const { data: hiddenProducts } = useGetProductsToHide();

  const {
    data,
    isQueryLoading: loading,
    isError: ccError,
    refetch
  } = useCCGetDealsMenu({});

  const error = ccError
    ? new ApolloError({ errorMessage: 'ccError' })
    : undefined;

  const menuQueryLoading = useRef(true);

  // Lineup data queries
  const { data: ccLineupData } = useCCGetProducts({
    categoryId: Categories.LINEUP
  });

  const lineupData = isLocalizedMenuPageSection(ccLineupData) ? ccLineupData : undefined;

  const isLineupValid = lineupData && (lineupData.productsForMainCategory.length > 0 || (lineupData.subCategories?.length ?? 0) > 0);

  const filteredDeals = useMemo(() => (hiddenProducts && hiddenProducts.deals ? data?.deals.filter((deal) => !hiddenProducts?.deals?.some((dealToHide) => deal.id.includes(dealToHide))) || [] : data?.deals || []), [data?.deals, hiddenProducts]);

  if (isLineupValid && filteredDeals) {
    filteredDeals.unshift(UPDATED_LINEUP_TILE);
  }
  menuQueryLoading.current = false;

  const dealTilesPrioritized = useDealTilePositioning({
    deals: filteredDeals
  });

  if (loading || error || menuQueryLoading.current) {
    return { deals: [], loading: Boolean(loading || menuQueryLoading.current) };
  }

  return { deals: dealTilesPrioritized, loading: Boolean(loading || menuQueryLoading.current), refetch };
};

export default useDealMenu;
